import { Injectable, ViewContainerRef, ComponentRef, NgZone } from '@angular/core';
import { Editor, Plugin } from '@ckeditor/ckeditor5-core';
import { EventInfo } from '@ckeditor/ckeditor5-utils';
import { EditorUI } from '@ckeditor/ckeditor5-ui';
import { ColorPickerTriggerComponent } from '../../../../../Common/Forms/components/ColorPickerTrigger/color-picker-trigger.component';
import { ColorProperty } from '../../../../../Common/Forms/components/AdvancedColorPickerPopover/service/recent-colors.service';
import { Subscription } from 'rxjs';

@Injectable()
export class CKColorPickerService {

    fontColocPicker!: EditorColorPicker;
    backgorundColocPicker!: EditorColorPicker;

    constructor(private zone: NgZone) {}

    setUpColorPickers(editor: Editor, viewRef: ViewContainerRef): void {
        this.fontColocPicker = new EditorColorPicker(editor, 'agprFontColorUI', viewRef, ColorProperty.TEXT_COLOR, this.zone);
        this.backgorundColocPicker = new EditorColorPicker(editor, 'agprFontBackgroundColorUI', viewRef, ColorProperty.BACKGROUND_COLOR, this.zone);

        (editor.plugins.get('agprFontColorUI') as Plugin)?.on('fontColorChange', (event: EventInfo, newValue: string) => {
            this.fontColocPicker.openPicker(newValue);
        });

        (editor.plugins.get('agprFontBackgroundColorUI') as Plugin)?.on('fontBackgroundColorChange', (event: EventInfo, newValue: string) => {
            this.backgorundColocPicker.openPicker(newValue);
        });
    }

    destory() {
        this.fontColocPicker.destroy();
        this.backgorundColocPicker.destroy();
    }
}

class EditorColorPicker {
    private readonly CK_TOOLBAR_OPEN_CLASS = 'ck-toolbar__items--open';

    private componentRef!: ComponentRef<ColorPickerTriggerComponent>;
    private editorColorButton!: HTMLButtonElement;
    private subscriptions: Subscription[] = [];

    constructor(
        editor: Editor,
        pluginName: string,
        viewRef: ViewContainerRef,
        private colorProperty: ColorProperty,
        private zone: NgZone
    ) {
        this.zone.run(() => {
            this.initPicker(editor, pluginName, viewRef);
            this.addEventListeners(editor, pluginName);
        });
    }

    public openPicker(color: string) {
        this.zone.run(() => {
            if (this.componentRef) {
                this.componentRef.instance.overlay.toggleOverlay();
                this.componentRef.instance.value = color;
                this.componentRef.instance.showFavoriteColors = true;
                this.componentRef.instance.showRecentColors = { show: true, property: this.colorProperty };
                this.updateColorPickerOverlay();
            }
        });
    }

    public destroy() {
        if (this.componentRef) {
            this.subscriptions.forEach(s => s.unsubscribe());
            this.componentRef.destroy();
        }
        document.removeEventListener('click', this.onDocumentClick);
    }

    private initPicker(editor: Editor, pluginName: string, viewRef: ViewContainerRef) {
        try {
            const plugin: { iconClass: string } | any = editor.plugins.get(pluginName);
            const editorUI: EditorUI = editor.ui;
            const toolbarElement = (editorUI.view as any)?.toolbar?.element;

            const iconClasses = plugin.iconClass.split(' ').map((c: string) => `.${c}`).join('');
            const editorButton = toolbarElement.querySelector(`${iconClasses}`) as HTMLButtonElement;

            this.editorColorButton = editorButton;
            this.componentRef = viewRef.createComponent(ColorPickerTriggerComponent);

            editorButton.appendChild(this.componentRef.location.nativeElement);
            this.updateColorPickerOverlay();
            document.addEventListener('click', this.onDocumentClick);
        } catch {

        }
    }

    private readonly onDocumentClick = () => {
        if (this.componentRef?.instance?.isOpen) {
            this.updateColorPickerOverlay();
        }
    };

    private addEventListeners(editor: Editor, pluginName: string) {
        if (!this.componentRef) {
            return;
        }

        const plugin: { inputEventName: string } | any = editor.plugins.get(pluginName);

        this.subscriptions.push(
            this.componentRef.instance.valueChange.subscribe((newValue: string) => {
                this.zone.run(() => editor.fire(plugin.inputEventName, newValue));
            }),
            this.componentRef.instance.isOpenChange.subscribe((isOpen: boolean) => {
                this.zone.run(() => {
                    if (isOpen) {
                        this.addActiveButtonClass();
                    } else {
                        editor.editing.view.focus();
                        this.removeActiveButtonClass();
                    }
                    this.updateColorPickerOverlay();
                });
            }),
            this.componentRef.instance.favoriteColorsService.getFavoriteColors$()
                .subscribe(() => {
                    this.zone.run(() => this.updateColorPickerOverlay());
                })
        );
    }

    private updateColorPickerOverlay() {
        this.zone.run(() => {
            requestAnimationFrame(() => this.componentRef.instance.cd.detectChanges());
        });
    }

    private addActiveButtonClass() {
        if (!this.editorColorButton.classList.contains(this.CK_TOOLBAR_OPEN_CLASS)) {
            this.editorColorButton.classList.add(this.CK_TOOLBAR_OPEN_CLASS);
        }
    }

    private removeActiveButtonClass() {
        this.editorColorButton.classList.remove(this.CK_TOOLBAR_OPEN_CLASS);
    }
}
